import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    DragDropContext,
    Draggable,
    type DraggableChildrenFn,
    type DraggableProvidedDraggableProps,
    type DraggableStateSnapshot,
    Droppable,
    type OnDragEndResponder,
    type OnDragStartResponder,
    type OnDragUpdateResponder,
} from '@hello-pangea/dnd';
import { gsap } from 'gsap';
import update from 'immutability-helper';
import { useTranslation } from 'react-i18next';
import {
    type ItemContent,
    type ItemProps,
    type ListRange,
    type ScrollSeekPlaceholderProps,
    Virtuoso,
    type VirtuosoProps,
} from 'react-virtuoso';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import { SidebarGroup, type SidebarYat } from '@app/helpers';
import { setSidebarGroups, storeDashboard } from '@app/store/actions/dashboard';

import YatSidebarGroup from './Group';
import YatSidebarItem from './Item';

interface YatSidebarListProps {
    fixedHeight?: number;
    isPrimaryYatPicker?: boolean;
    onClick?(): void;
    onDragStateChange?: (dragging: boolean) => void;
}

interface HeightPreservingItemProps extends ItemProps {
    children?: React.ReactNode;
}

type VirtuosoScrollerRef = VirtuosoProps<SidebarGroup | SidebarYat, unknown>['scrollerRef'];

function getHeight() {
    const beforeTabletWidth = 767;
    const { innerWidth } = window;

    // According to Eugene, these numbers match the height of the
    // `Create Yat` button in the Sidebar.
    return innerWidth > beforeTabletWidth ? innerHeight - 100 : innerHeight - 70;
}

export function YatSidebarList({
    fixedHeight,
    isPrimaryYatPicker = false,
    onClick,
    onDragStateChange,
}: YatSidebarListProps) {
    const { t: translate } = useTranslation(['words']);
    const dispatch = useAppDispatch();

    const { currentEmoji } = useAppSelector((state) => state.userEmojis);
    const { sidebar } = useAppSelector((state) => state.dashboard);
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);

    const sidebarChildrenRefs = useRef<HTMLDivElement[]>([]);

    const [isDragging, setIsDragging] = useState(false);
    const [isDraggingGroup, setIsDraggingGroup] = useState(false);
    const [combineHoverId, setCombineHoverId] = useState('');

    const [listRange, setListRange] = useState<ListRange>({ startIndex: 0, endIndex: 0 });
    const [sidebarEmoji, setSidebarEmoji] = useState<EmojiId>();
    const [height, setHeight] = useState(() => getHeight());

    const isDragAllowed = Boolean(onDragStateChange);

    useEffect(() => {
        if (isPrimaryYatPicker) {
            setSidebarEmoji(primaryYat);
        } else {
            setSidebarEmoji(currentEmoji);
        }
    }, [currentEmoji, primaryYat, isPrimaryYatPicker]);

    useEffect(() => {
        onDragStateChange?.(isDragging);
    }, [isDragging, onDragStateChange]);

    // Add array of ref's for all first-level sidebar items.
    useEffect(() => {
        sidebarChildrenRefs.current = sidebarChildrenRefs.current.slice(0, Number(sidebar.children.length));
    }, [sidebar]);

    useEffect(() => {
        function resize() {
            const newHeight = getHeight();
            setHeight(newHeight);
        }

        window.addEventListener('resize', resize);

        return () => {
            window.removeEventListener('resize', resize);
        };
    }, []);

    useEffect(() => {
        const { startIndex, endIndex } = listRange;

        if (startIndex >= endIndex || !sidebar.hasOpenSubGroup) return;

        const sidebarState = sidebar;

        if (
            !sidebarState.children
                .slice(startIndex, endIndex + 1)
                .filter((child) => child.type === 'SidebarGroup')
                // TODO: Avoid this typecast.
                .find((child) => (child as SidebarGroup).open)
        ) {
            const index = sidebarState.children.findIndex((child) => {
                if (child.type === 'SidebarYat') return false;

                // TODO: Avoid this typecast.
                if ((child as SidebarGroup).open) return true;

                // Expecting `return false` here?
            });

            if (index > -1) {
                // TODO: Avoid this typecast.
                (sidebarState.children[index] as SidebarGroup).open = false;
                dispatch(setSidebarGroups(sidebarState));
            }
        }
    }, [listRange, sidebar, dispatch]);

    const onDragStart: OnDragStartResponder = useCallback(
        ({ source }) => {
            if (!isDragAllowed) return;

            setIsDragging(true);

            const draggableItem = sidebar.children[source.index];
            if (draggableItem && draggableItem.type === 'SidebarYat') return;

            setIsDraggingGroup(true);
        },
        [sidebar, isDragAllowed],
    );

    const onDragUpdate: OnDragUpdateResponder = useCallback(
        ({ combine }) => {
            if (!isDragAllowed) return;

            setCombineHoverId(combine?.draggableId ?? '');
        },
        [setCombineHoverId, isDragAllowed],
    );

    const onDragEnd: OnDragEndResponder = useCallback(
        ({ destination, source, combine, type }) => {
            if (!isDragAllowed) return;

            setIsDragging(false);
            setIsDraggingGroup(false);
            setCombineHoverId('');

            const draggableItem = sidebar.children[source.index];

            if (type.startsWith('GROUP')) {
                if (!destination) return;

                const groupIndex = Number(type.split('_').pop());
                const draggableGroupItem = (sidebar.children[groupIndex] as SidebarGroup).children[source.index];

                dispatch(
                    storeDashboard(
                        update(sidebar, {
                            children: {
                                [groupIndex]: {
                                    children: {
                                        $splice: [
                                            [source.index, 1],
                                            [destination.index, 0, draggableGroupItem],
                                        ],
                                    },
                                },
                            },
                        }),
                    ),
                );

                return;
            }

            if (combine) {
                // Combining items to groups
                const droppableItem = sidebar.children[combine.draggableId];

                if (droppableItem.type === 'SidebarYat') {
                    // Create new group
                    const newGroup: (SidebarYat | SidebarGroup)[] = [droppableItem, draggableItem];
                    const newSidebarGroup = new SidebarGroup({ children: newGroup, parent: sidebar });

                    // TODO: `combine.draggableId` is a string... We should avoid
                    // converting strings to numbers as it is unreliable.
                    const convertedId = parseInt(combine.draggableId, 10);
                    const newGroupIndex = convertedId > source.index ? convertedId - 1 : combine.draggableId;

                    dispatch(
                        storeDashboard(
                            update(sidebar, {
                                children: {
                                    $splice: [
                                        [source.index, 1],
                                        [newGroupIndex, 1, newSidebarGroup],
                                    ],
                                },
                            }),
                        ),
                    );
                } else {
                    // Add item to existing group
                    const updatedGroup = (sidebar.children[combine.draggableId] as SidebarGroup).children;

                    updatedGroup.push(sidebar.children[source.index]);

                    const updatedSidebarGroup = new SidebarGroup({
                        children: updatedGroup,
                        parent: sidebar,
                        name: (sidebar.children[combine.draggableId] as SidebarGroup).name,
                    });

                    dispatch(
                        storeDashboard(
                            update(sidebar, {
                                children: {
                                    $splice: [
                                        [combine.draggableId, 1, updatedSidebarGroup],
                                        [source.index, 1],
                                    ],
                                },
                            }),
                        ),
                    );
                }
            } else {
                // Change order of items
                if (!destination) return;

                if (destination.droppableId === source.droppableId && destination.index === source.index) return;

                dispatch(
                    storeDashboard(
                        update(sidebar, {
                            children: {
                                $splice: [
                                    [source.index, 1],
                                    [destination.index, 0, draggableItem],
                                ],
                            },
                        }),
                    ),
                );
            }
        },
        [dispatch, sidebar, isDragAllowed],
    );

    const getDraggableClassNames = (snapshot: DraggableStateSnapshot, index: number) => {
        const classNames = ['yat-sidebar__item'];

        if (combineHoverId === index + '') {
            classNames.push('yat-sidebar__item--combine-hover');
        }

        if (snapshot.isDragging) {
            classNames.push('yat-sidebar__item--is-dragging');
        }

        if (snapshot.isDropAnimating && snapshot.combineWith) {
            classNames.push('yat-sidebar__item--is-combined');
        }

        return classNames.join(' ');
    };

    const getDraggableStyle = (
        style: DraggableProvidedDraggableProps['style'],
        { dropAnimation, combineWith, isDropAnimating }: DraggableStateSnapshot,
    ) => {
        if (!combineWith || !isDropAnimating) return style;

        // Add scale animation when combining current draggable into another draggable
        const moveTo = dropAnimation?.moveTo ?? { x: 0, y: 0 };
        const curve = dropAnimation?.curve ?? 0;
        const duration = dropAnimation?.duration ?? 0;

        const translate = `translate(${moveTo.x}px, ${moveTo.y}px)`;
        const scale = 'scale(0.5)';
        const newDuration = duration + 0.25;

        const combinableItem = sidebarChildrenRefs.current[combineWith];

        // If the combineWith is an item (not a group), animate the item into a group.
        const itemElementBackground = combinableItem.querySelectorAll(
            '.yat-sidebar__item > div > .yat-sidebar-item .yat-sidebar-item__background',
        );

        const itemElementChilds = combinableItem.querySelectorAll(
            '.yat-sidebar__item > div > .yat-sidebar-item > div:not(.yat-sidebar-item__background)',
        );

        if (itemElementChilds && itemElementBackground) {
            gsap.timeline().to(itemElementChilds, { opacity: 0, duration: 0.1 }).to(itemElementBackground, {
                width: 60,
                scale: 1,
                borderRadius: '33%',
                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                duration: 0.3,
                ease: 'power4.out',
            });

            gsap.set([itemElementBackground, itemElementChilds], { clearProps: 'all', delay: newDuration + 0.1 });
        }

        return {
            ...style,
            transform: `${translate} ${scale}`,
            transition: `all ${curve} ${newDuration}s`,
        };
    };

    const HeightPreservingItem = useMemo(() => {
        /* eslint-disable react/display-name */
        return ({ children, ...props }: HeightPreservingItemProps) => {
            const height = props['data-known-size'] || undefined;

            return (
                <div {...props} style={{ height }} className="yat-sidebar__virtual-item">
                    {children}
                </div>
            );
        };
    }, []);

    const PlaceHolderItem = useMemo(() => {
        return ({ height }: ScrollSeekPlaceholderProps) => {
            return (
                <div>
                    <div style={{ height: height }}>{translate('words:loading')}</div>
                </div>
            );
        };
    }, [translate]);

    // If fixedHeight is set, use that, otherwise use dynamic height
    const heightToUse = fixedHeight ? fixedHeight : height;

    const cloneRenderer: DraggableChildrenFn = (provided, snapshot, rubric) => {
        const { innerRef, draggableProps, dragHandleProps } = provided;
        const { index } = rubric.source;

        const item = sidebar.children[index];

        const itemMarkup = item ? (
            item.type === 'SidebarYat' ? (
                <YatSidebarItem
                    yat={item as SidebarYat}
                    isPrimaryYatPicker={isPrimaryYatPicker}
                    isActive={!snapshot.isDragging && sidebarEmoji === item.toString()}
                />
            ) : (
                <YatSidebarGroup group={item as SidebarGroup} index={index.toString()} />
            )
        ) : null;

        return (
            <div
                ref={innerRef}
                {...draggableProps}
                {...dragHandleProps}
                className="yat-sidebar--is-expanded"
                style={getDraggableStyle(draggableProps.style, snapshot)}
            >
                <li className={getDraggableClassNames(snapshot, index)}>
                    <div ref={(element: HTMLDivElement) => (sidebarChildrenRefs.current[index] = element)}>
                        {itemMarkup}
                    </div>
                </li>
            </div>
        );
    };

    const itemRenderer: ItemContent<SidebarGroup | SidebarYat, unknown> = (index, item) => {
        return (
            <Draggable
                key={`sidebar-draggable-${index}`}
                index={index}
                draggableId={index.toString()}
                isDragDisabled={sidebar.hasOpenSubGroup || !isDragAllowed}
            >
                {(provided, snapshot) => (
                    <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={getDraggableClassNames(snapshot, index)}
                    >
                        <div ref={(element: HTMLDivElement) => (sidebarChildrenRefs.current[index] = element)}>
                            {item ? (
                                item.type === 'SidebarYat' ? (
                                    <YatSidebarItem
                                        yat={item as SidebarYat}
                                        isPrimaryYatPicker={isPrimaryYatPicker}
                                        isActive={!isDragging && sidebarEmoji === item.toString()}
                                        onClick={onClick}
                                    />
                                ) : (
                                    <YatSidebarGroup
                                        group={item as SidebarGroup}
                                        index={index + ''}
                                        isUpdateDisabled={!isDragAllowed}
                                        isPrimaryYatPicker={isPrimaryYatPicker}
                                        onClick={onClick}
                                    />
                                )
                            ) : null}
                        </div>
                    </li>
                )}
            </Draggable>
        );
    };

    return (
        <DragDropContext onDragStart={onDragStart} onDragUpdate={onDragUpdate} onDragEnd={onDragEnd}>
            <Droppable
                droppableId="droppableSidebar"
                mode="virtual"
                isCombineEnabled={!isDraggingGroup && !sidebar.hasOpenSubGroup}
                renderClone={cloneRenderer}
            >
                {(provided) => (
                    <Virtuoso
                        className="yat-sidebar-virtual"
                        data={sidebar.children}
                        style={{ height: heightToUse }}
                        overscan={heightToUse}
                        rangeChanged={setListRange}
                        scrollerRef={provided.innerRef as VirtuosoScrollerRef}
                        components={{
                            Item: HeightPreservingItem,
                            ScrollSeekPlaceholder: PlaceHolderItem,
                        }}
                        itemContent={itemRenderer}
                    />
                )}
            </Droppable>
        </DragDropContext>
    );
}
